import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import QuoteForm from './components/QuoteForm';
import QuoteStats from './components/QuoteStats';
import QuoteExport from './components/QuoteExport';
import FavoritesList from './components/FavoritesList';
import DailyQuoteReminder from './components/DailyQuoteReminder';
import EmailSubscription from './components/EmailSubscription';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AdSenseAd from './components/AdSenseAd';
import './App.css';
import axios from 'axios';

function App() {
  const [quote, setQuote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(-1);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('favoriteQuotes');
    const savedDarkMode = localStorage.getItem('darkMode');
    const savedHistory = localStorage.getItem('quoteHistory');
    
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode));
    }
    if (savedHistory) {
      setQuoteHistory(JSON.parse(savedHistory));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('favoriteQuotes', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    document.body.className = darkMode ? 'dark-mode' : '';
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('quoteHistory', JSON.stringify(quoteHistory));
  }, [quoteHistory]);

  const handleGenerateQuote = async (category, language) => {
    if (!language) {
      setError('Prosím vyberte jazyk');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('https://motivacna-backend.vercel.app/generate-quote', { 
        category, 
        language 
      });
      
      const newQuote = {
        id: Date.now(),
        text: response.data.quote,
        category: category || 'Náhodný',
        language: language,
        date: new Date().toISOString()
      };
      
      setQuote(newQuote.text);
      setQuoteHistory(prev => [newQuote, ...prev.slice(0, 49)]); // Keep last 50 quotes
      setCurrentQuoteIndex(0);
      
    } catch (error) {
      console.error('Chyba pri generovaní citátu:', error);
      setError('Nastala chyba pri generovaní citátu. Skúste to znova.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToFavorites = () => {
    if (quote && !favorites.some(fav => fav.text === quote)) {
      const newFavorite = {
        id: Date.now(),
        text: quote,
        category: quoteHistory[currentQuoteIndex]?.category || 'Náhodný',
        language: quoteHistory[currentQuoteIndex]?.language || 'sk',
        date: new Date().toLocaleDateString()
      };
      setFavorites([...favorites, newFavorite]);
    }
  };

  const handleRemoveFromFavorites = (id) => {
    setFavorites(favorites.filter(fav => fav.id !== id));
  };

  const handleShareQuote = async () => {
    if (navigator.share && quote) {
      try {
        await navigator.share({
          title: 'Motivačný citát',
          text: quote,
          url: window.location.href
        });
      } catch (err) {
        console.log('Zdieľanie nie je podporované');
        navigator.clipboard.writeText(quote);
        alert('Citát bol skopírovaný do schránky!');
      }
    } else if (quote) {
      navigator.clipboard.writeText(quote);
      alert('Citát bol skopírovaný do schránky!');
    }
  };

  const handlePreviousQuote = () => {
    if (currentQuoteIndex < quoteHistory.length - 1) {
      const newIndex = currentQuoteIndex + 1;
      setCurrentQuoteIndex(newIndex);
      setQuote(quoteHistory[newIndex].text);
    }
  };

  const handleNextQuote = () => {
    if (currentQuoteIndex > 0) {
      const newIndex = currentQuoteIndex - 1;
      setCurrentQuoteIndex(newIndex);
      setQuote(quoteHistory[newIndex].text);
    }
  };

  const isFavorite = quote && favorites.some(fav => fav.text === quote);

  return (
    <div className={`App ${darkMode ? 'dark' : ''}`}>
      <div className="container">
        <Navbar 
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          favorites={favorites}
          showFavorites={showFavorites}
          setShowFavorites={setShowFavorites}
          showStats={showStats}
          setShowStats={setShowStats}
          showPrivacy={showPrivacy}
          setShowPrivacy={setShowPrivacy}
          showTerms={showTerms}
          setShowTerms={setShowTerms}
          quoteHistory={quoteHistory}
        />
        
        <DailyQuoteReminder 
          onGenerateQuote={handleGenerateQuote}
          darkMode={darkMode}
        />
        
        {/* Bočné reklamy - zobrazujú sa len na desktop */}
        <AdSenseAd 
          adSlot="3399233034"  
          placement="sidebar-left"
        />
        
        <AdSenseAd 
          adSlot="2979865982"  
          placement="sidebar-right"
        />
        
        {!showFavorites && !showStats && !showPrivacy && !showTerms ? (
          <div className="main-content">
            <QuoteForm 
              onGenerate={handleGenerateQuote} 
              loading={loading}
              darkMode={darkMode}
            />
            
            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {quote && (
              <div className="quote-card">
                <div className="quote-text">
                  <p>"{quote}"</p>
                </div>
                
                <div className="quote-navigation">
                  <button 
                    onClick={handlePreviousQuote}
                    disabled={currentQuoteIndex >= quoteHistory.length - 1}
                    className="nav-button"
                    title="Predchádzajúci citát"
                  >
                    ← Predchádzajúci
                  </button>
                  
                  <span className="quote-counter">
                    {currentQuoteIndex + 1} / {quoteHistory.length}
                  </span>
                  
                  <button 
                    onClick={handleNextQuote}
                    disabled={currentQuoteIndex <= 0}
                    className="nav-button"
                    title="Ďalší citát"
                  >
                    Ďalší →
                  </button>
                </div>
                
                <div className="quote-actions">
                  <button 
                    onClick={handleAddToFavorites}
                    className={`action-button ${isFavorite ? 'favorited' : ''}`}
                    disabled={isFavorite}
                    title="Pridať do obľúbených"
                  >
                    {isFavorite ? '❤️ Pridané' : '🤍 Pridať'}
                  </button>
                  
                  <button 
                    onClick={handleShareQuote}
                    className="action-button"
                    title="Zdieľať citát"
                  >
                    📤 Zdieľať
                  </button>
                </div>
              </div>
            )}
            
            <EmailSubscription darkMode={darkMode} />
          </div>
        ) : showStats ? (
          <QuoteStats 
            quoteHistory={quoteHistory}
            favorites={favorites}
            darkMode={darkMode}
          />
        ) : showPrivacy ? (
          <PrivacyPolicy darkMode={darkMode} />
        ) : showTerms ? (
          <TermsOfService darkMode={darkMode} />
        ) : showFavorites ? (
          <div className="favorites-section">
            <h2>❤️ Obľúbené citáty ({favorites.length})</h2>
            {favorites.length === 0 ? (
              <div className="empty-favorites">
                <p>Zatiaľ nemáte žiadne obľúbené citáty.</p>
                <button 
                  onClick={() => setShowFavorites(false)}
                  className="generate-button"
                >
                  Generovať prvý citát
                </button>
              </div>
            ) : (
              <>
                <FavoritesList 
                  favorites={favorites}
                  onRemoveFromFavorites={handleRemoveFromFavorites}
                  darkMode={darkMode}
                />
                <QuoteExport favorites={favorites} darkMode={darkMode} />
              </>
            )}
          </div>
        ) : null}
        
        {/* Spodná sticky reklama - vždy viditeľná */}
        <AdSenseAd 
          adSlot="3836301013"  
          placement="bottom"
          closeable={true}
        />
      </div>
    </div>
  );
}

export default App;
